import {
  cloneElement,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import { cn } from 'utils/style-utils';
import {
  toSideAndAlign,
  type OverlayPlacement,
  type OverlayProps,
} from './overlay.types';

const lightClassName =
  'bg-white p-2 text-gray-900 shadow-md ring-1 ring-neutral-300 [&>span>svg]:bg-white [&>span>svg]:fill-white';

interface OverlayTooltipProps {
  /** Nothing is shown while it's empty. */
  content: ReactNode;
  /** Must accept a ref and pointer handlers. */
  children: ReactElement;
  placement?: OverlayPlacement;
  delayDuration?: number;
  /** `light` is a white card, for content richer than a line of text. */
  tone?: 'dark' | 'light';
  overlayProps?: OverlayProps;
  className?: string;
  /** Only open while the child's text is cut off; the child gets a ref. */
  whenTruncated?: boolean;
}

/**
 * A DS hover tooltip. It sits above `ControlledPopover`, so it can be used in
 * popover content too.
 */
export function OverlayTooltip({
  content,
  children,
  placement = 'top',
  delayDuration = 800,
  tone = 'dark',
  overlayProps,
  className,
  whenTruncated = false,
}: OverlayTooltipProps) {
  const triggerRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);

  if (!content) return children;
  const { side, align } = toSideAndAlign(placement);

  const handleOpenChange = (next: boolean) => {
    const element = triggerRef.current;
    setOpen(next && !!element && element.scrollWidth > element.clientWidth);
  };

  return (
    <Tooltip
      delayDuration={delayDuration}
      {...(whenTruncated && { open, onOpenChange: handleOpenChange })}
    >
      <TooltipTrigger asChild>
        {whenTruncated ? cloneElement(children, { ref: triggerRef }) : children}
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        className={cn('z-5002', tone === 'light' && lightClassName, className)}
        {...overlayProps}
      >
        {content}
      </TooltipContent>
    </Tooltip>
  );
}
